import React, { useState, useEffect } from 'react';
import api, { filialeService } from '../services/api';
import {
  IconeGraphique, IconeHorloge,
} from './IconesSVG';

// Roles autorises a exporter. Doit rester coherent avec PORTEE_PAR_ROLE dans
// backend/src/services/exportService.js — le backend reste seul juge, ceci ne
// fait qu'eviter d'afficher un bouton qui repondrait 403.
export const ROLES_EXPORT = [
  'admin', 'preparateur_memo_holding', 'pdg', 'dg_filiale', 'preparateur_memo_filiale',
];

// Roles dont la portee couvre toutes les filiales : eux seuls ont un choix a
// faire, les autres sont de toute facon ramenes a la leur cote serveur.
const ROLES_TOUTES_FILIALES = ['admin', 'preparateur_memo_holding', 'pdg'];

const TYPES_DOCUMENT = [
  { valeur: '', libelle: 'Tous les types' },
  { valeur: 'expression_besoin', libelle: 'Fiche Expression de Besoin' },
  { valeur: 'bon_commande', libelle: 'Bon de commande' },
  { valeur: 'frais_mission', libelle: 'Frais de mission' },
  { valeur: 'facture', libelle: 'Facture' },
  { valeur: 'regularisation', libelle: 'Regularisation' },
];

const iso = (d) => d.toISOString().slice(0, 10);
const jjmmaaaa = (s) => s.split('-').reverse().join('');

// Extrait le message d'un corps d'erreur renvoye en Blob.
const messageErreur = async (e) => {
  const corps = e.response?.data ?? e.data;
  if (corps instanceof Blob) {
    try { return JSON.parse(await corps.text()).message; } catch (x) { /* corps non JSON */ }
  }
  return corps?.message || e.message || "Échec de l'export";
};

export default function ExportExcel({ role }) {
  const maintenant = new Date();
  const [ouvert, setOuvert] = useState(false);
  const [dateDebut, setDateDebut] = useState(iso(new Date(maintenant.getFullYear(), 0, 1)));
  const [dateFin, setDateFin] = useState(iso(maintenant));
  const [typeDocument, setTypeDocument] = useState('');
  const [filialeId, setFilialeId] = useState('');
  const [filiales, setFiliales] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [etape, setEtape] = useState('');
  const [erreur, setErreur] = useState('');

  const toutesFiliales = ROLES_TOUTES_FILIALES.includes(role);

  // La liste n'est chargee qu'a l'ouverture, et seulement pour qui peut choisir.
  useEffect(() => {
    if (!ouvert || !toutesFiliales || filiales.length) return;
    filialeService.getAll()
      .then((res) => setFiliales(res.data || []))
      .catch(() => { /* le selecteur reste sur « toutes » */ });
  }, [ouvert, toutesFiliales, filiales.length]);

  if (!ROLES_EXPORT.includes(role)) return null;

  const datesValides = dateDebut && dateFin && dateDebut <= dateFin;

  const telecharger = async () => {
    if (!datesValides) { setErreur('La date de debut doit preceder la date de fin.'); return; }
    setEnCours(true); setErreur('');
    setEtape('Extraction des demandes…');
    try {
      const params = new URLSearchParams({ date_debut: dateDebut, date_fin: dateFin });
      if (typeDocument) params.set('type_document', typeDocument);
      if (filialeId) params.set('filiale_id', filialeId);

      setEtape('Construction du classeur (4 onglets)…');
      // responseType blob : sans cela axios interpreterait le binaire comme du
      // texte et le classeur serait corrompu. L'intercepteur global renvoie
      // deja response.data, donc `reponse` est le Blob.
      const reponse = await api.get(`/export/rapport-activite?${params.toString()}`, {
        responseType: 'blob',
      });
      const blob = reponse instanceof Blob ? reponse : new Blob([reponse]);

      setEtape('Telechargement…');
      const nom = `JETA_GED_Rapport_${jjmmaaaa(dateDebut)}_${jjmmaaaa(dateFin)}.xlsx`;
      const url = URL.createObjectURL(blob);
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = nom;
      document.body.appendChild(lien);
      lien.click();
      document.body.removeChild(lien);
      URL.revokeObjectURL(url);
      setOuvert(false);
    } catch (e) {
      // En mode blob, le corps d'erreur est lui aussi un Blob : le message JSON
      // du serveur doit etre relu, sinon l'utilisateur ne verrait qu'un
      // « Network Error » generique la ou le serveur explique le refus.
      setErreur(await messageErreur(e));
    } finally {
      setEnCours(false);
      setEtape('');
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOuvert(true)} style={s.bouton} title="Exporter vers Excel">
        <IconeGraphique taille={13} /> Export Excel
      </button>

      {ouvert && (
        <div style={s.fond} onClick={(e) => { if (e.target === e.currentTarget && !enCours) setOuvert(false); }}>
          <div className="modale-mobile" style={s.boite}>
            <div style={s.titre}><IconeGraphique taille={14} /> Rapport d&apos;activité</div>
            <div style={s.sousTitre}>
              Quatre onglets : tableau de bord, liste des demandes, rapport par filiale, suivi des délais.
            </div>

            <div style={s.grilleDates}>
              <div>
                <label style={s.label} htmlFor="export-debut">Date de début</label>
                <input id="export-debut" type="date" value={dateDebut} max={dateFin}
                  onChange={(e) => setDateDebut(e.target.value)} style={s.champ} />
              </div>
              <div>
                <label style={s.label} htmlFor="export-fin">Date de fin</label>
                <input id="export-fin" type="date" value={dateFin} min={dateDebut} max={iso(maintenant)}
                  onChange={(e) => setDateFin(e.target.value)} style={s.champ} />
              </div>
            </div>

            <label style={s.label} htmlFor="export-type">Type de document</label>
            <select id="export-type" value={typeDocument} onChange={(e) => setTypeDocument(e.target.value)} style={s.champ}>
              {TYPES_DOCUMENT.map((t) => <option key={t.valeur} value={t.valeur}>{t.libelle}</option>)}
            </select>

            {toutesFiliales && (
              <>
                <label style={s.label} htmlFor="export-filiale">Filiale</label>
                <select id="export-filiale" value={filialeId} onChange={(e) => setFilialeId(e.target.value)} style={s.champ}>
                  <option value="">Toutes les filiales</option>
                  {filiales.map((f) => (
                    <option key={f._id || f.id} value={f._id || f.id}>{f.code} — {f.nom}</option>
                  ))}
                </select>
              </>
            )}

            {!datesValides && (
              <div style={s.avertissement}>La date de début doit précéder la date de fin.</div>
            )}
            {erreur && <div style={s.erreur}>{erreur}</div>}
            {enCours && etape && (
              <div style={s.progression}><IconeHorloge taille={13} /> {etape}</div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button type="button" onClick={() => setOuvert(false)} disabled={enCours} style={s.annuler}>
                Annuler
              </button>
              <button type="button" onClick={telecharger} disabled={enCours || !datesValides} style={s.valider}>
                {enCours ? <><IconeHorloge taille={13} /> Génération…</> : 'Générer le rapport'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const s = {
  bouton: { background: '#1E8449', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  fond: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2500, padding: 16 },
  boite: { background: '#fff', borderRadius: 14, padding: 26, width: 400, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', fontFamily: 'inherit' },
  titre: { fontSize: 16, fontWeight: 800, color: '#0D1B2A', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 },
  sousTitre: { fontSize: 12, color: '#888', lineHeight: 1.45, marginBottom: 6 },
  grilleDates: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  label: { display: 'block', fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5, marginTop: 12 },
  champ: { width: '100%', padding: '9px 10px', borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', background: '#fff', minHeight: 40 },
  avertissement: { background: '#FEF9E7', border: '1px solid #9A7D0A', color: '#9A7D0A', borderRadius: 8, padding: '9px 12px', fontSize: 12, marginTop: 14 },
  erreur: { background: '#FDEDEC', border: '1px solid #C0392B', color: '#96281B', borderRadius: 8, padding: '9px 12px', fontSize: 12, marginTop: 14 },
  progression: { background: '#EAF4FB', border: '1px solid #1A5E8A', color: '#1A5E8A', borderRadius: 8, padding: '9px 12px', fontSize: 12, marginTop: 14, display: 'flex', alignItems: 'center', gap: 6 },
  annuler: { flex: 1, padding: '11px', borderRadius: 9, border: '1px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', minHeight: 44 },
  valider: { flex: 2, padding: '11px', borderRadius: 9, border: 'none', background: '#1E8449', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', minHeight: 44 },
};
